/**
 * The one place the desktop breakpoint is written down in JavaScript.
 *
 * `lg` = 1024 px is the app-wide split between the phone surfaces (bottom tab
 * bar, bottom sheets) and the desktop ones (sidebar, inline rows, popovers).
 * It lived as four separate `const LG_BREAKPOINT = 1024` declarations plus four
 * copies of the same `isDesktopViewport()` helper — in `picker-sheet.js`,
 * `wizard-summary.js`, `reminder-bell.js` and `pages/reminders.js`. Nothing
 * stopped one of them from drifting away from the others, or from the Tailwind
 * `lg:` classes that decide the same question in CSS.
 *
 * Prefer a CSS `lg:` pair wherever both surfaces can simply coexist in the DOM
 * — that reacts to a resize for free. Use this only where the two
 * presentations are genuinely different DOM (a bottom sheet versus an anchored
 * popover), which CSS cannot express.
 */
export const LG_BREAKPOINT = 1024;

/**
 * Whether the viewport is at or above `lg` **right now**. Call it at the moment
 * of the decision, never at module load — the answer changes when the window
 * is resized or the device is rotated.
 *
 * @returns {boolean} false in a non-browser context, so server-side and jsdom
 *   callers fall back to the phone presentation.
 */
export function isDesktopViewport() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(`(min-width: ${LG_BREAKPOINT}px)`).matches;
}
